import { useState, useCallback, useEffect } from 'react';

function useTableSorting({ updateQueryParams, getQueryParams, initialSortColumn, initialSortType }) {
  const [sortColumn, setSortColumn] = useState(initialSortColumn);
  const [sortType, setSortType] = useState(initialSortType);

  // Initialize sort from URL parameters or use defaults
  useEffect(() => {
    const params = getQueryParams();
    if (params.sortColumn && params.sortType) {
      setSortColumn(params.sortColumn);
      setSortType(params.sortType);
    } else if (initialSortColumn && initialSortType) {
      // Set default sort if no URL params and defaults are provided
      setSortColumn(initialSortColumn);
      setSortType(initialSortType);
      
      // Update URL with default sort parameters
      updateQueryParams({
        sortColumn: initialSortColumn,
        sortType: initialSortType
      }, { replace: true });
    }
  }, [getQueryParams, initialSortColumn, initialSortType, updateQueryParams]);
  const handleSortColumn = useCallback((dataKey, newSortType) => {
    setSortColumn(dataKey);
    setSortType(newSortType);

    // Update URL with sort parameters
    const sortParams = {};
    if (dataKey && newSortType) {
      sortParams.sortColumn = dataKey;
      sortParams.sortType = newSortType;
    } else {
      sortParams.sortColumn = '';
      sortParams.sortType = '';
    }

    updateQueryParams(sortParams, { replace: true });
  }, [updateQueryParams]);

  const applySorting = useCallback((data) => {
    if (!sortColumn || !sortType || !data || data.length === 0) {
      return data;
    }

    return [...data].sort((a, b) => {
      let aValue = a[sortColumn];
      let bValue = b[sortColumn];

      // Handle null/undefined values: always sort them last for asc, first for desc
      const aIsNil = aValue === null || aValue === undefined;
      const bIsNil = bValue === null || bValue === undefined;
      if (aIsNil && bIsNil) return 0;
      if (aIsNil) return sortType === "asc" ? 1 : -1;
      if (bIsNil) return sortType === "asc" ? -1 : 1;

      // Only treat as date if both are strings and look like dates (ISO or yyyy-mm-dd)
      const isDateString = v => typeof v === 'string' && /\d{4}-\d{2}-\d{2}/.test(v);
      if (isDateString(aValue) && isDateString(bValue)) {
        const aDate = new Date(aValue);
        const bDate = new Date(bValue);
        const aIsDate = !isNaN(aDate.getTime());
        const bIsDate = !isNaN(bDate.getTime());
        if (aIsDate && bIsDate) {
          if (aDate.getTime() === bDate.getTime()) return 0;
          if (aDate.getTime() < bDate.getTime()) return sortType === "asc" ? -1 : 1;
          if (aDate.getTime() > bDate.getTime()) return sortType === "asc" ? 1 : -1;
        }
      }

      // Fallback: always compare as trimmed strings for natural, case-insensitive sorting
      const aStr = String(aValue).trim();
      const bStr = String(bValue).trim();
      const cmp = aStr.localeCompare(bStr, undefined, { sensitivity: 'base', numeric: true });
      return sortType === "asc" ? cmp : -cmp;
    });
  }, [sortColumn, sortType]);

  return {
    sortColumn,
    sortType,
    handleSortColumn,
    applySorting
  };
}

export default useTableSorting;
